import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { exportFile } from '@/core/http/helpers';
import { ExportFormatCode } from '@/core/models/practice.models';
import type {
  ApplicationTestingActivity,
  ApplicationTestingCommentCreated,
  ApplicationTestingTicketCreated,
  ApplicationTestingTicketDetail,
  ApplicationTestingTicketQuery,
  ApplicationTestingTicketResponse,
  ApplicationTestingTicketRow,
  NewApplicationTestingTicket,
} from '@/core/models/applicationTesting.models';

/** Raising tickets asking for the app itself to be improved (`api/v1/ApplicationTesting/`). */

const base = 'ApplicationTesting/';

/** The wire value the `priority` form field expects: "0" Low, "1" High, "3" Medium. */
const PRIORITY_CODE: Record<NewApplicationTestingTicket['priority'], string> = {
  low: '0',
  high: '1',
  medium: '3',
};

export const applicationTestingService = {
  list: (query: ApplicationTestingTicketQuery) => api.post<ApplicationTestingTicketResponse>(`${base}list`, query),

  getDetail: (id: string) => api.get<ApplicationTestingTicketDetail>(`${base}${id}`),

  /** For previewing an image attachment inline (not a download). */
  getAttachment: (attachmentId: string) => api.get<Blob>(`${base}attachment/${attachmentId}`, { responseType: 'blob' }),

  /** Saves the attachment to disk, named from the response's Content-Disposition header. */
  downloadAttachment: (attachmentId: string) =>
    apiRequest<Blob>('GET', `${base}attachment/${attachmentId}`, { responseType: 'blob' }).then(download),

  /** 0 Open, 1 In Progress, 2 Completed. */
  setStatus: (id: string, status: number) => api.post<ApplicationTestingTicketRow>(`${base}setStatus`, { id, status }),

  /** 0 Low, 1 High, 2 Extreme. */
  setPriority: (id: string, priority: number) =>
    api.post<ApplicationTestingTicketRow>(`${base}setPriority`, { id, priority }),

  /**
   * Downloads the ticked tickets (all of them when `ids` is empty) as a spreadsheet, rows in the order given. Tags only
   * live in this browser, so each ticket's tags travel with the request.
   */
  exportTickets: (ids: string[], tags: Record<string, string[]>, format = ExportFormatCode.Excel) =>
    exportFile(`${base}export`, { format, ids, tags }, false),

  /** Saves the ticket's whole tag set (replacing the previous one); the server records the change in its history. */
  setTags: (id: string, tags: string[]) => api.put<ApplicationTestingTicketRow>(`${base}${id}/tags`, { tags }),

  /** The ticket's history - status, priority, tag and comment changes - newest first. */
  getActivity: (id: string) => api.get<ApplicationTestingActivity[]>(`${base}${id}/activity`),

  /** Edits a ticket's subject/description in place (both fields together, like the server's row). */
  updateTicket: (id: string, subject: string, description: string) =>
    api.post<ApplicationTestingTicketRow>(`${base}update`, { id, subject, description }),

  /** Multipart upload: adds one or more attachments straight to the ticket (not to a comment). No response body. */
  addTicketAttachments: (ticketId: string, files: File[]) => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file, file.name));

    return api.post<void>(`${base}${ticketId}/attachments`, form);
  },

  /** Multipart upload: no Content-Type header, so the browser sets the boundary. */
  saveTicket: (ticket: NewApplicationTestingTicket) => {
    const form = new FormData();
    form.append('subject', ticket.subject);
    form.append('description', ticket.description);
    form.append('priority', PRIORITY_CODE[ticket.priority]);
    form.append('status', String(ticket.status));
    ticket.files.forEach((file) => form.append('files', file, file.name));

    return api.post<ApplicationTestingTicketCreated>(`${base}create`, form);
  },

  /** For previewing an image comment attachment inline (not a download). */
  getCommentAttachment: (attachmentId: string) =>
    api.get<Blob>(`${base}comment-attachment/${attachmentId}`, { responseType: 'blob' }),

  /** Saves the comment attachment to disk, named from the response's Content-Disposition header. */
  downloadCommentAttachment: (attachmentId: string) =>
    apiRequest<Blob>('GET', `${base}comment-attachment/${attachmentId}`, { responseType: 'blob' }).then(download),

  /** Multipart upload: text and any number of files, same limits as raising a ticket. */
  addComment: (ticketId: string, text: string, files: File[]) => {
    const form = new FormData();
    form.append('text', text);
    files.forEach((file) => form.append('files', file, file.name));

    return api.post<ApplicationTestingCommentCreated>(`${base}${ticketId}/comments`, form);
  },
};
